import { useEffect, useMemo, useRef, useState } from "react";
import { adminApiRequest } from "../utils/adminApi";

const createOptionValue = () => ({
  temp_id: `value_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  label: "",
  price: "",
  is_default: false,
  is_active: true,
});

const createOptionGroup = (isAddon = false) => ({
  temp_id: `group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  name: "",
  is_required: true,
  is_active: true,
  is_reusable: false,
  is_addon: isAddon,
  reusable_group_id: null,
  values: [createOptionValue()],
});

const initialForm = {
  category_id: "",
  name: "",
  details: "",
  base_price: "",
  requires_photo_upload: false,
  max_uploads: "1",
  allows_custom_text: false,
  custom_text_label: "",
  custom_text_max_length: "",
  is_active: true,
  option_groups: [],
  addons: [],
};

function mapReusableGroupToFormGroup(group, isAddon = false) {
  return {
    temp_id: `group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    id: null,
    name: group.name || "",
    is_required: Number(group.is_required) === 1,
    is_active: Number(group.is_active) === 1,
    is_reusable: true,
    is_addon: isAddon,
    reusable_group_id: group.id,
    values: Array.isArray(group.values)
      ? group.values.map((value) => ({
          temp_id: `value_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          id: value.id || null,
          reusable_value_id: value.id || null,
          label: value.label || "",
          price:
            value.price === null || value.price === undefined
              ? ""
              : String(value.price),
          is_default: Number(value.is_default) === 1,
          is_active:
            value.is_active === undefined
              ? true
              : Number(value.is_active) === 1,
        }))
      : [],
  };
}

export default function useProductForm({
  isOpen,
  editingProduct,
  onSaved,
  onClose,
}) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState("");
  const [existingImage, setExistingImage] = useState(null);
  const [shouldRemoveExistingImage, setShouldRemoveExistingImage] =
    useState(false);

  const [reusableGroups, setReusableGroups] = useState([]);
  const [loadingReusableGroups, setLoadingReusableGroups] = useState(false);
  const [reusableGroupsError, setReusableGroupsError] = useState("");
  const [selectedReusableGroupId, setSelectedReusableGroupId] = useState("");
  const [selectedReusableAddonId, setSelectedReusableAddonId] = useState("");

  const previewRef = useRef("");

  const isEditMode = useMemo(
    () => Boolean(editingProduct?.id),
    [editingProduct],
  );

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;

    const fetchReusableGroups = async () => {
      try {
        setLoadingReusableGroups(true);
        setReusableGroupsError("");

        const res = await adminApiRequest({
          script: "fetch_shop_option_groups",
        });

        if (!res?.success) {
          throw new Error(
            res?.message ||
              res?.error ||
              "Failed to fetch reusable option groups.",
          );
        }

        if (!isMounted) return;

        setReusableGroups(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (!isMounted) return;
        setReusableGroups([]);
        setReusableGroupsError(
          err?.message || "Unable to load reusable option groups.",
        );
      } finally {
        if (isMounted) {
          setLoadingReusableGroups(false);
        }
      }
    };

    fetchReusableGroups();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (editingProduct) {
      const mapGroups = (groupsList, isAddon = false) => {
        return Array.isArray(groupsList)
          ? groupsList.map((group) => ({
              temp_id:
                group.temp_id ||
                `group_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
              id: group.id || null,
              name: group.name || "",
              is_required: Number(group.is_required) === 1,
              is_active:
                group.is_active === undefined
                  ? true
                  : Number(group.is_active) === 1,
              is_addon: isAddon,
              is_reusable: Boolean(
                group.is_reusable ||
                  group.reusable_group_id ||
                  group.option_group_id,
              ),
              reusable_group_id:
                group.reusable_group_id ||
                group.option_group_id ||
                group.id ||
                null,
              values:
                Array.isArray(group.values) && group.values.length > 0
                  ? group.values.map((value) => ({
                      temp_id:
                        value.temp_id ||
                        `value_${Date.now()}_${Math.random()
                          .toString(36)
                          .slice(2, 8)}`,
                      id: value.id || null,
                      reusable_value_id:
                        value.reusable_value_id || value.id || null,
                      label: value.label || "",
                      price:
                        value.price === null || value.price === undefined
                          ? ""
                          : String(value.price),
                      is_default: Number(value.is_default) === 1,
                      is_active:
                        value.is_active === undefined
                          ? true
                          : Number(value.is_active) === 1,
                    }))
                  : [createOptionValue()],
            }))
          : [];
      };

      setForm({
        category_id: editingProduct.category_id
          ? String(editingProduct.category_id)
          : "",
        name: editingProduct.name || "",
        details: editingProduct.details || "",
        base_price:
          editingProduct.base_price === null ||
          editingProduct.base_price === undefined
            ? ""
            : String(editingProduct.base_price),
        requires_photo_upload:
          Number(editingProduct.requires_photo_upload) === 1,
        max_uploads:
          editingProduct.max_uploads === null ||
          editingProduct.max_uploads === undefined
            ? "1"
            : String(editingProduct.max_uploads),
        allows_custom_text: Number(editingProduct.allows_custom_text) === 1,
        custom_text_label: editingProduct.custom_text_label || "",
        custom_text_max_length:
          editingProduct.custom_text_max_length === null ||
          editingProduct.custom_text_max_length === undefined
            ? ""
            : String(editingProduct.custom_text_max_length),
        is_active: Number(editingProduct.is_active) === 1,
        option_groups: mapGroups(editingProduct.option_groups, false),
        addons: mapGroups(editingProduct.addons, true),
      });

      setExistingImage(
        editingProduct.image_url
          ? {
              id: editingProduct.image_id || null,
              image_url: editingProduct.image_url,
            }
          : null,
      );
    } else {
      setForm(initialForm);
      setExistingImage(null);
    }

    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = "";
    }

    setSelectedImage(null);
    setSelectedImagePreview("");
    setShouldRemoveExistingImage(false);
    setSelectedReusableGroupId("");
    setSelectedReusableAddonId("");
    setError("");
    setSaving(false);
  }, [isOpen, editingProduct]);

  useEffect(() => {
    return () => {
      if (previewRef.current) {
        URL.revokeObjectURL(previewRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "allows_custom_text" && !checked) {
        next.custom_text_label = "";
        next.custom_text_max_length = "";
      }

      if (name === "requires_photo_upload") {
        if (checked) {
          next.max_uploads =
            prev.max_uploads && Number(prev.max_uploads) >= 1
              ? prev.max_uploads
              : "1";
        } else {
          next.max_uploads = "";
        }
      }

      if (name === "max_uploads") {
        if (value === "") {
          next.max_uploads = "";
        } else {
          const parsed = Number(value);
          if (!Number.isNaN(parsed)) {
            if (parsed < 1) next.max_uploads = "1";
            else if (parsed > 10) next.max_uploads = "10";
          }
        }
      }

      return next;
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;

    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = "";
    }

    setSelectedImage(file);

    if (file) {
      const previewUrl = URL.createObjectURL(file);
      previewRef.current = previewUrl;
      setSelectedImagePreview(previewUrl);
    } else {
      setSelectedImagePreview("");
    }
  };

  const removeSelectedImage = () => {
    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = "";
    }

    setSelectedImage(null);
    setSelectedImagePreview("");
  };

  const removeExistingImage = () => {
    setExistingImage(null);
    setShouldRemoveExistingImage(true);
  };

  const addOptionGroup = (isAddon = false) => {
    const key = isAddon ? 'addons' : 'option_groups';
    setForm((prev) => ({
      ...prev,
      [key]: [...prev[key], createOptionGroup(isAddon)],
    }));
  };

  const addReusableOptionGroup = (groupId, isAddon = false) => {
    const key = isAddon ? 'addons' : 'option_groups';
    const numericGroupId = Number(groupId);
    if (!numericGroupId) return;

    const matchedGroup = reusableGroups.find(
      (group) => Number(group.id) === numericGroupId,
    );

    if (!matchedGroup) return;

    setForm((prev) => {
      const alreadyExists = prev[key].some(
        (group) => Number(group.reusable_group_id) === numericGroupId,
      );

      if (alreadyExists) return prev;

      return {
        ...prev,
        [key]: [
          ...prev[key],
          mapReusableGroupToFormGroup(matchedGroup, isAddon),
        ],
      };
    });

    if (isAddon) {
      setSelectedReusableAddonId("");
    } else {
      setSelectedReusableGroupId("");
    }
  };

  const removeOptionGroup = (groupIndex, isAddon = false) => {
    const key = isAddon ? 'addons' : 'option_groups';
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].filter(
        (_, index) => index !== groupIndex,
      ),
    }));
  };

  const updateOptionGroup = (groupIndex, field, value, isAddon = false) => {
    const key = isAddon ? 'addons' : 'option_groups';
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].map((group, index) =>
        index === groupIndex ? { ...group, [field]: value } : group,
      ),
    }));
  };

  const addOptionValue = (groupIndex, isAddon = false) => {
    const key = isAddon ? 'addons' : 'option_groups';
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].map((group, index) =>
        index === groupIndex
          ? { ...group, values: [...group.values, createOptionValue()] }
          : group,
      ),
    }));
  };

  const removeOptionValue = (groupIndex, valueIndex, isAddon = false) => {
    const key = isAddon ? 'addons' : 'option_groups';
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].map((group, index) =>
        index === groupIndex
          ? {
              ...group,
              values: group.values.filter(
                (_, innerIndex) => innerIndex !== valueIndex,
              ),
            }
          : group,
      ),
    }));
  };

  const updateOptionValue = (groupIndex, valueIndex, field, value, isAddon = false) => {
    const key = isAddon ? 'addons' : 'option_groups';
    setForm((prev) => ({
      ...prev,
      [key]: prev[key].map((group, index) => {
        if (index !== groupIndex) return group;

        if (field === "is_default" && value === true) {
          return {
            ...group,
            values: group.values.map((optionValue, innerIndex) => ({
              ...optionValue,
              is_default: innerIndex === valueIndex,
            })),
          };
        }

        return {
          ...group,
          values: group.values.map((optionValue, innerIndex) =>
            innerIndex === valueIndex
              ? { ...optionValue, [field]: value }
              : optionValue,
          ),
        };
      }),
    }));
  };

  const validateForm = () => {
    if (!form.category_id) return "Please select a category.";
    if (!form.name.trim()) return "Product name is required.";

    if (
      form.base_price !== "" &&
      (Number.isNaN(Number(form.base_price)) || Number(form.base_price) < 0)
    ) {
      return "Base price must be a valid number.";
    }

    if (
      form.requires_photo_upload &&
      (form.max_uploads === "" ||
        Number.isNaN(Number(form.max_uploads)) ||
        Number(form.max_uploads) < 1 ||
        Number(form.max_uploads) > 10)
    ) {
      return "Customer upload limit must be between 1 and 10.";
    }

    if (
      form.allows_custom_text &&
      form.custom_text_max_length !== "" &&
      (Number.isNaN(Number(form.custom_text_max_length)) ||
        Number(form.custom_text_max_length) < 1)
    ) {
      return "Custom text max length must be at least 1.";
    }

    if (!isEditMode && !selectedImage) {
      return "Please select a product image.";
    }

    const validateGroups = (groups, groupTypeName) => {
      for (let i = 0; i < groups.length; i += 1) {
        const group = groups[i];

        if (!group.name.trim()) {
          return `${groupTypeName} category ${i + 1} must have a name.`;
        }

        if (!Array.isArray(group.values) || group.values.length === 0) {
          return `${groupTypeName} category "${group.name || i + 1}" must have at least one option value.`;
        }

        let defaultCount = 0;

        for (let j = 0; j < group.values.length; j += 1) {
          const value = group.values[j];

          if (!value.label.trim()) {
            return `Option value ${j + 1} in "${group.name || `category ${i + 1}`}" must have a label.`;
          }

          if (value.price !== "" && Number.isNaN(Number(value.price))) {
            return `Price for "${value.label || `value ${j + 1}`}" must be a valid number.`;
          }

          if (Number(value.price) < 0) {
            return `Price for "${value.label || `value ${j + 1}`}" cannot be negative.`;
          }

          if (value.is_default) {
            defaultCount += 1;
          }
        }

        if (defaultCount > 1) {
          return `Only one default option is allowed in "${group.name}".`;
        }
      }
      return "";
    };

    const optionsError = validateGroups(form.option_groups, "Option");
    if (optionsError) return optionsError;
    
    const addonsError = validateGroups(form.addons, "Addon");
    if (addonsError) return addonsError;

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError("");

      const formData = new FormData();

      formData.append("category_id", Number(form.category_id));
      formData.append("name", form.name.trim());
      formData.append("details", form.details.trim());
      formData.append(
        "base_price",
        form.base_price === "" ? "" : Number(form.base_price),
      );
      formData.append(
        "requires_photo_upload",
        form.requires_photo_upload ? 1 : 0,
      );
      formData.append(
        "max_uploads",
        form.requires_photo_upload ? Number(form.max_uploads) : "",
      );
      formData.append("allows_custom_text", form.allows_custom_text ? 1 : 0);
      formData.append(
        "custom_text_label",
        form.allows_custom_text ? form.custom_text_label.trim() : "",
      );
      formData.append(
        "custom_text_max_length",
        form.allows_custom_text && form.custom_text_max_length !== ""
          ? Number(form.custom_text_max_length)
          : "",
      );
      formData.append("is_active", form.is_active ? 1 : 0);

      if (isEditMode) {
        formData.append("product_id", editingProduct.id);
      }

      const allGroups = [...form.option_groups, ...form.addons];

      formData.append(
        "option_groups",
        JSON.stringify(
          allGroups.map((group) => ({
            id: group.id || null,
            reusable_group_id: group.reusable_group_id || null,
            is_reusable: group.is_reusable ? 1 : 0,
            name: group.name.trim(),
            is_required: group.is_required ? 1 : 0,
            is_active: group.is_active ? 1 : 0,
            is_addon: group.is_addon ? 1 : 0,
            values: group.values.map((value) => ({
              id: value.id || null,
              reusable_value_id: value.reusable_value_id || null,
              label: value.label.trim(),
              price: value.price === "" ? 0 : Number(value.price),
              is_default: value.is_default ? 1 : 0,
              is_active: value.is_active ? 1 : 0,
            })),
          })),
        ),
      );

      formData.append(
        "remove_existing_image",
        !selectedImage && shouldRemoveExistingImage ? "1" : "0",
      );

      if (selectedImage) {
        formData.append("image", selectedImage);
      }

      const res = await adminApiRequest({
        script: isEditMode ? "update_shop_product" : "create_shop_product",
        formData,
      });

      if (!res?.success) {
        throw new Error(
          res?.message ||
            res?.error ||
            `Failed to ${isEditMode ? "update" : "create"} product.`,
        );
      }

      await onSaved();
      onClose();
    } catch (err) {
      setError(
        err.message || `Unable to ${isEditMode ? "update" : "create"} product.`,
      );
    } finally {
      setSaving(false);
    }
  };

  return {
    form,
    setForm,
    saving,
    error,
    setError,
    selectedImage,
    selectedImagePreview,
    existingImage,
    isEditMode,
    reusableGroups,
    loadingReusableGroups,
    reusableGroupsError,
    selectedReusableGroupId,
    setSelectedReusableGroupId,
    selectedReusableAddonId,
    setSelectedReusableAddonId,
    handleChange,
    handleImageChange,
    removeSelectedImage,
    removeExistingImage,
    addOptionGroup,
    addReusableOptionGroup,
    removeOptionGroup,
    updateOptionGroup,
    addOptionValue,
    removeOptionValue,
    updateOptionValue,
    handleSubmit,
  };
}